<script setup lang="ts">
    import {ref, onMounted} from "vue";

    import DVideo from "../atoms/DVideo.vue";
    import DImg from "../atoms/DImg.vue";
    import DContainer from "../atoms/DContainer.vue";
    import DText from "../atoms/DText.vue";
    import DButton from "../atoms/DButton.vue";

    import DCTA from "../molecules/DCTA.vue";

    import {
        isVideoContentType,
        isImageContentType,
    } from "@songfinch/utils/src/validators";

    import type {
        BaseBlockFields,
        NormalizedAsset,
        BaseCtaType,
    } from "@songfinch/types";

    const props = defineProps<
        BaseBlockFields & {
            headline: string;
            subHeadline: string;
            media?: NormalizedAsset;
            mobileMedia?: NormalizedAsset;
            ctaSecondary?: BaseCtaType;
        }
    >();

    const activeMedia = ref(props.media);
    const videoRef = ref();
    const videoIsPlaying = ref(true);

    const playPauseVideo = () => {
        if (videoIsPlaying.value) {
            videoRef.value?.$el.pause();
        } else {
            videoRef.value?.$el.play();
        }

        videoIsPlaying.value = !videoIsPlaying.value;
    };


    function updateVideoUrl(query: MediaQueryList) {
        if (!props.mobileMedia) return; 
        activeMedia.value = query.matches ? props.mobileMedia : props.media;
    }

    onMounted(async () => {
        const mediaQuery = window.matchMedia("(max-width: 768px)");
        updateVideoUrl(mediaQuery);
    });
</script>

<template>
    <DContainer
        class="d-hero-media-wrapper"
        display="grid"
        theme="dark"
        :data-test-id="dataTestId"
        :data-block-placement="dataBlockPlacement"
    >
        <template v-if="activeMedia">
            <DVideo
                v-if="isVideoContentType(activeMedia.contentType)"
                ref="videoRef"
                class="d-hero-media-background-video"
                variant="fill-section"
                :src="activeMedia.url"
                :title="activeMedia.title"
                playsinline
                autobuffer
                autoplay
                disableremoteplayback
                muted
                loop
                preload="auto"
            />

            <DImg
                v-else-if="isImageContentType(activeMedia.contentType)"
                variant="fill"
                :src="activeMedia.url"
                :alt="activeMedia.title"
                class="d-hero-media-background-image"
            />
        </template>

        <DContainer class="d-hero-media-background-opacity"/>

        <DContainer class="d-hero-media-content" display="grid">
            <DText
                class="d-hero-media-headline"
                type="h1"
                variant="hero-1"
                spacing="m"
            >
                {{headline}}
            </DText>
            <DText
                v-if="subHeadline"
                class="d-hero-media-sub-headline"
                type="p"
                variant="p-2"
            >
                {{subHeadline}}
            </DText>
            <DContainer class="d-hero-media-cta-wrapper">
                <DCTA
                    v-if="ctaSecondary"
                    :cta="ctaSecondary"
                    :data-test-id="`${dataTestId}-cta-secondary`"
                    :internal-title="`${internalTitle}-cta-secondaty`"
                />
                <DCTA
                    v-if="cta"
                    :cta="cta"
                    :data-test-id="`${dataTestId}-cta`"
                    :internal-title="`${internalTitle}-cta`"
                />
            </DContainer>

            <DButton
                v-if="media && isVideoContentType(media.contentType)"
                :data-test-id="`${dataTestId}-pause-video`"
                class="d-hero-video-button"
                theme="dark"
                variant="secondary-outline"
                size="small"
                :icon="videoIsPlaying ? 'pause' : 'play'"
                label="Video"
                @click="playPauseVideo()"
            />
        </DContainer>
    </DContainer>
</template>

<style lang="scss" scoped>
.d-hero-media-wrapper {
  width: 100%;
  position: relative;
  height: 633px;

  @include md {
    height: 800px;
  }
}

.d-hero-media-background-opacity,
.d-hero-media-background-video,
.d-hero-media-background-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.d-hero-media-background-opacity {
  z-index: 2;
  background-color: rgba(0, 0, 0, 0.4);
}

.d-hero-media-content {
  width: 100%;
  padding: 40px 20px;
  z-index: 3;
  align-self: flex-end;

  @include md {
    max-width: 1440px;
    margin: 0 auto;
    grid-template-columns: 534px 1fr;
    padding: 100px 30px;
  }
}

.d-hero-media-headline,
.d-hero-media-sub-headline,
.d-hero-media-cta-wrapper {
  grid-column: 1;
  align-items: center;

  @include md {
    display: flex;
    justify-items: flex-start;
  }
}

.d-hero-media-cta-wrapper {
  display: grid;
  grid-template-columns: auto auto;
  column-gap: 4px;

  @include md {
    display: flex;
    justify-items: flex-start;
  }
}

.d-hero-media-sub-headline {
  margin-bottom: 24px;
}

.d-hero-video-button {
  position: absolute;
  top: 20px;
  right: 20px;

  @include md {
    grid-column: 2;
    position: relative;
    top: initial;
    right: initial;
    justify-self: flex-end;
  }
}
</style>
